import React from 'react';
import { JobPosting, FilterState, JobDepartment, JobType } from '../types';
import { Search, MapPin, Clock, Star, ArrowRight, Briefcase } from 'lucide-react';

interface JobListProps {
  jobs: JobPosting[];
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onSelectJob: (job: JobPosting) => void;
}

const DEPARTMENTS: JobDepartment[] = [
  'All',
  'Development',
  'Infrastructure',
  'Moderation',
  'Game Design',
  'Marketing',
  'Systems',
];

const JOB_TYPES: JobType[] = [
  'All',
  'Full-time',
  'Part-time',
  'Volunteer / Staff',
  'Contract',
];

export const JobList: React.FC<JobListProps> = ({
  jobs,
  filters,
  onFilterChange,
  onSelectJob,
}) => {
  return (
    <div>
      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search className="search-icon" size={18} />
          <input
            type="text"
            className="search-input"
            placeholder="Search positions by keyword, skill, or department..."
            value={filters.search}
            onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
          />
        </div>

        <select
          className="filter-select"
          value={filters.department}
          onChange={(e) => onFilterChange({ ...filters, department: e.target.value as JobDepartment })}
        >
          <option value="All">All Departments</option>
          {DEPARTMENTS.filter(d => d !== 'All').map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filters.type}
          onChange={(e) => onFilterChange({ ...filters, type: e.target.value as JobType })}
        >
          <option value="All">All Employment Types</option>
          {JOB_TYPES.filter(t => t !== 'All').map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {/* Jobs Grid */}
      {jobs.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'var(--bg-card)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-color)',
          }}
        >
          <Briefcase size={48} color="var(--text-dim)" style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '1.2rem', fontWeight: 700 }}>No positions found</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '6px' }}>
            Try adjusting your search keywords or clearing filters.
          </p>
        </div>
      ) : (
        <div className="jobs-grid">
          {jobs.map((job) => {
            const isFeatured = Boolean(job.is_featured);
            const deptClass = job.department.replace(/\s+/g, '');

            return (
              <div
                key={job.id}
                className={`job-card ${isFeatured ? 'featured' : ''}`}
                onClick={() => onSelectJob(job)}
                style={{ cursor: 'pointer' }}
              >
                {isFeatured && (
                  <span className="featured-pill">
                    <Star size={12} fill="var(--primary-cyan)" /> Featured Role
                  </span>
                )}

                <div className="job-header">
                  <span className={`dept-badge ${deptClass}`}>
                    {job.department}
                  </span>
                  <h3 className="job-title">{job.title}</h3>

                  <div className="job-meta-row">
                    <span className="job-meta-item">
                      <MapPin size={14} color="var(--primary-cyan)" />
                      {job.location}
                    </span>
                    <span className="job-meta-item">
                      <Clock size={14} color="var(--accent-purple)" />
                      {job.type}
                    </span>
                  </div>
                </div>

                <p className="job-description-preview">{job.description}</p>

                <div className="job-card-footer">
                  <span className="salary-tag">{job.salary_range || 'Competitive'}</span>
                  <button
                    className="btn-secondary"
                    style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectJob(job);
                    }}
                  >
                    View Details <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
